import { Field, type FieldLabelProps } from "@ark-ui/react";
import { Info } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import {
  Tooltip,
  type TooltipProps,
} from "../../components/tooltip/Tooltip.js";

export type LabelOptionProps =
  | string
  | {
      label?: string;
      optional?: boolean;
      infoMessage?: React.ReactNode;
      tooltipProps?: Omit<TooltipProps, "content" | "children">;
    };

export type FormLabelClassNames = {
  root?: string;
  asterisk?: string;
  labelControls?: string;
  optional?: string;
  labelWrapper?: string;
};

interface FormLabelProps extends Omit<FieldLabelProps, "as"> {
  label?: LabelOptionProps;
  size?: "xs" | "sm" | "md" | "lg";
  error?: boolean | string;
  asterisk?: boolean;
  classNames?: FormLabelClassNames;
  component?: React.ElementType;
}

const FormLabel = React.forwardRef<
  React.ElementRef<typeof Field.Label>,
  FormLabelProps
>((props, ref) => {
  const {
    className,
    label,
    size = "md",
    asterisk = false,
    error,
    classNames,
    component,
    ...rest
  } = props;

  const LabelComponent = component || Field.Label;

  if (!label) {
    return null;
  }
  return (
    <React.Fragment>
      {typeof label === "string" ? (
        <LabelComponent
          data-size={size}
          className={clsx("ox-reset ox-FormLabel", className, classNames?.root)}
          {...rest}
          data-error={error ? true : false}
          ref={ref}
        >
          {label}
          {asterisk ? (
            <span
              className={clsx(
                "ox-reset ox-FormLabelAsterisk",
                classNames?.asterisk,
              )}
            >
              {" "}
              *
            </span>
          ) : null}
        </LabelComponent>
      ) : (
        <div
          className={clsx(
            "ox-reset ox-FormLabelControls",
            classNames?.labelControls,
          )}
        >
          <span
            className={clsx(
              "ox-reset oxTextFieldLabelWrapper",
              classNames?.labelWrapper,
            )}
          >
            <LabelComponent
              data-size={size}
              data-error={error ? true : false}
              className={clsx(
                "ox-reset ox-FormLabel",
                className,
                classNames?.root,
              )}
              {...rest}
              ref={ref}
            >
              {label?.label}
              {asterisk ? (
                <span
                  className={clsx(
                    "ox-reset ox-FormLabelAsterisk",
                    classNames?.asterisk,
                  )}
                >
                  {" "}
                  *
                </span>
              ) : null}
            </LabelComponent>

            {label?.infoMessage ? (
              <Tooltip
                content={label?.infoMessage}
                openDelay={50}
                lazyMount
                mode="dark"
                unmountOnExit
                {...label.tooltipProps}
                positioning={{
                  placement: "top",
                  ...label?.tooltipProps?.positioning,
                }}
              >
                <span
                  role="button"
                  data-size={size}
                  className="ox-reset ox-FormLabelInfo"
                >
                  <Info fill="currentColor" />
                </span>
              </Tooltip>
            ) : null}
          </span>

          {label?.optional ? (
            <span
              data-size={size}
              className={clsx(
                "ox-reset ox-FormLabelOptional",
                classNames?.optional,
              )}
            >
              Optional
            </span>
          ) : null}
        </div>
      )}
    </React.Fragment>
  );
});

export { FormLabel };
export type { FormLabelProps };
